import React, { useState, createContext, useContext } from 'react';
// ─── Indicator ───────────────────────────────────────────────
export interface Indicator {
  id: string;
  name: string;
  description: string;
  type: 'leading' | 'lagging';
  scope: 'activity' | 'project' | 'portfolio' | 'enterprise';
  sourceType: 'risk_parameter' | 'risk_score' | 'derived_metric' | 'external_metric' | 'behavioral_metric';
  sourceParameterId?: string;
  unit: string;
  directionOfGood: 'higher_is_better' | 'lower_is_better' | 'in_range';
  cadence: 'realtime' | 'daily' | 'weekly' | 'monthly';
  currentValue: number;
  history: {
    date: string;
    value: number;
  }[];
  isPromotableToKpi: boolean;
  dataFreshness: string;
}
// ─── Objective ───────────────────────────────────────────────
export interface Objective {
  id: string;
  name: string;
  description: string;
  type: 'safety_outcome' | 'operational_response' | 'compliance' | 'engagement';
  scope: 'project' | 'portfolio' | 'enterprise';
  ownerRole: string;
  timeHorizon: 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'ongoing';
  startDate: string;
  endDate?: string;
  status: 'active' | 'draft' | 'archived';
  priority: number;
  primaryKpiIds: string[];
  supportingIndicatorIds: string[];
  actionItemIds: string[];
}
// ─── KPI ─────────────────────────────────────────────────────
export interface KPI {
  id: string;
  name: string;
  indicatorId: string;
  objectiveId?: string;
  scope: 'project' | 'portfolio' | 'enterprise';
  owner: string;
  targetValue: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  baselineValue: number;
  currentValue: number;
  targetDate: string;
  evaluationPeriod: 'weekly' | 'monthly' | 'quarterly' | 'annual';
  status: 'on_track' | 'at_risk' | 'behind' | 'achieved';
  actionItemIds: string[];
  linkedInsightId?: string;
  linkedInsightTitle?: string;
  createdAt: Date;
  history: {
    date: string;
    value: number;
  }[];
}
// ─── Action Item ─────────────────────────────────────────────
export interface ActionItem {
  id: string;
  title: string;
  description: string;
  type: 'hotspot_mitigation' | 'kpi_improvement' | 'objective_initiative' | 'policy' | 'training' | 'inspection';
  triggerSource: 'hotspot' | 'indicator' | 'kpi' | 'objective' | 'ai_recommendation' | 'manual';
  triggerSourceId?: string;
  objectiveId?: string;
  kpiId?: string;
  hotspotId?: string;
  owner: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  dueDate: string;
  status: 'open' | 'in_progress' | 'blocked' | 'done' | 'canceled';
  rationale: string;
  expectedImpact: string;
  completedAt?: string;
  effectivenessReview?: string;
  createdAt: Date;
}
// ─── Mock Indicators (12) ────────────────────────────────────
const MOCK_INDICATORS: Indicator[] = [
// Leading
{
  id: 'ind-1',
  name: 'Average Risk Score',
  description: 'Mean risk score across all active hotspots',
  type: 'leading',
  scope: 'project',
  sourceType: 'risk_score',
  unit: 'score',
  directionOfGood: 'lower_is_better',
  cadence: 'daily',
  currentValue: 6.2,
  history: [{
    date: 'W1',
    value: 7.1
  }, {
    date: 'W2',
    value: 6.8
  }, {
    date: 'W3',
    value: 6.5
  }, {
    date: 'W4',
    value: 6.2
  }],
  isPromotableToKpi: true,
  dataFreshness: '2 hours ago'
}, {
  id: 'ind-2',
  name: 'PTP Completion Rate',
  description: 'Percentage of pre-task plans completed on time',
  type: 'leading',
  scope: 'project',
  sourceType: 'behavioral_metric',
  unit: '%',
  directionOfGood: 'higher_is_better',
  cadence: 'daily',
  currentValue: 72,
  history: [{
    date: 'W1',
    value: 65
  }, {
    date: 'W2',
    value: 68
  }, {
    date: 'W3',
    value: 70
  }, {
    date: 'W4',
    value: 72
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 hour ago'
}, {
  id: 'ind-3',
  name: 'Average Response Time',
  description: 'Mean time from hotspot identification to first action',
  type: 'leading',
  scope: 'project',
  sourceType: 'derived_metric',
  unit: 'hrs',
  directionOfGood: 'lower_is_better',
  cadence: 'daily',
  currentValue: 14,
  history: [{
    date: 'W1',
    value: 22
  }, {
    date: 'W2',
    value: 18
  }, {
    date: 'W3',
    value: 16
  }, {
    date: 'W4',
    value: 14
  }],
  isPromotableToKpi: true,
  dataFreshness: '3 hours ago'
}, {
  id: 'ind-4',
  name: 'Mitigation Rate',
  description: 'Percentage of identified risks with at least one mitigation action taken',
  type: 'leading',
  scope: 'project',
  sourceType: 'derived_metric',
  unit: '%',
  directionOfGood: 'higher_is_better',
  cadence: 'weekly',
  currentValue: 78,
  history: [{
    date: 'W1',
    value: 70
  }, {
    date: 'W2',
    value: 73
  }, {
    date: 'W3',
    value: 76
  }, {
    date: 'W4',
    value: 78
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 day ago'
}, {
  id: 'ind-5',
  name: 'Control Implementation Rate',
  description: 'Percentage of required controls reported as implemented',
  type: 'leading',
  scope: 'project',
  sourceType: 'derived_metric',
  unit: '%',
  directionOfGood: 'higher_is_better',
  cadence: 'weekly',
  currentValue: 82,
  history: [{
    date: 'W1',
    value: 75
  }, {
    date: 'W2',
    value: 78
  }, {
    date: 'W3',
    value: 80
  }, {
    date: 'W4',
    value: 82
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 day ago'
}, {
  id: 'ind-6',
  name: 'Unresolved Hotspot Count',
  description: 'Number of hotspots with status Open for more than 24 hours',
  type: 'leading',
  scope: 'project',
  sourceType: 'derived_metric',
  unit: 'count',
  directionOfGood: 'lower_is_better',
  cadence: 'daily',
  currentValue: 5,
  history: [{
    date: 'W1',
    value: 12
  }, {
    date: 'W2',
    value: 9
  }, {
    date: 'W3',
    value: 7
  }, {
    date: 'W4',
    value: 5
  }],
  isPromotableToKpi: true,
  dataFreshness: '2 hours ago'
}, {
  id: 'ind-7',
  name: 'Planning Quality Score',
  description: 'Average planning quality parameter across all activities',
  type: 'leading',
  scope: 'project',
  sourceType: 'risk_parameter',
  sourceParameterId: 'planning_quality',
  unit: 'score',
  directionOfGood: 'higher_is_better',
  cadence: 'weekly',
  currentValue: 7.4,
  history: [{
    date: 'W1',
    value: 6.8
  }, {
    date: 'W2',
    value: 7.0
  }, {
    date: 'W3',
    value: 7.2
  }, {
    date: 'W4',
    value: 7.4
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 day ago'
}, {
  id: 'ind-8',
  name: 'Inspection Completion Rate',
  description: 'Percentage of scheduled inspections completed on time',
  type: 'leading',
  scope: 'project',
  sourceType: 'behavioral_metric',
  unit: '%',
  directionOfGood: 'higher_is_better',
  cadence: 'weekly',
  currentValue: 88,
  history: [{
    date: 'W1',
    value: 82
  }, {
    date: 'W2',
    value: 84
  }, {
    date: 'W3',
    value: 86
  }, {
    date: 'W4',
    value: 88
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 day ago'
},
// Lagging
{
  id: 'ind-9',
  name: 'TRIR',
  description: 'Total Recordable Incident Rate per 200,000 work hours',
  type: 'lagging',
  scope: 'enterprise',
  sourceType: 'external_metric',
  unit: 'rate',
  directionOfGood: 'lower_is_better',
  cadence: 'monthly',
  currentValue: 2.8,
  history: [{
    date: 'M1',
    value: 3.2
  }, {
    date: 'M2',
    value: 3.0
  }, {
    date: 'M3',
    value: 2.9
  }, {
    date: 'M4',
    value: 2.8
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 week ago'
}, {
  id: 'ind-10',
  name: 'LRIR',
  description: 'Lost Time Incident Rate per 200,000 work hours',
  type: 'lagging',
  scope: 'enterprise',
  sourceType: 'external_metric',
  unit: 'rate',
  directionOfGood: 'lower_is_better',
  cadence: 'monthly',
  currentValue: 1.1,
  history: [{
    date: 'M1',
    value: 1.5
  }, {
    date: 'M2',
    value: 1.3
  }, {
    date: 'M3',
    value: 1.2
  }, {
    date: 'M4',
    value: 1.1
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 week ago'
}, {
  id: 'ind-11',
  name: 'SIF Count',
  description: 'Number of Serious Injuries and Fatalities in the period',
  type: 'lagging',
  scope: 'enterprise',
  sourceType: 'external_metric',
  unit: 'count',
  directionOfGood: 'lower_is_better',
  cadence: 'monthly',
  currentValue: 0,
  history: [{
    date: 'M1',
    value: 1
  }, {
    date: 'M2',
    value: 0
  }, {
    date: 'M3',
    value: 0
  }, {
    date: 'M4',
    value: 0
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 week ago'
}, {
  id: 'ind-12',
  name: 'Lost Workday Count',
  description: 'Total workdays lost due to incidents in the period',
  type: 'lagging',
  scope: 'enterprise',
  sourceType: 'external_metric',
  unit: 'days',
  directionOfGood: 'lower_is_better',
  cadence: 'monthly',
  currentValue: 12,
  history: [{
    date: 'M1',
    value: 28
  }, {
    date: 'M2',
    value: 18
  }, {
    date: 'M3',
    value: 15
  }, {
    date: 'M4',
    value: 12
  }],
  isPromotableToKpi: true,
  dataFreshness: '1 week ago'
}];
// ─── Mock Objectives (3) ─────────────────────────────────────
const MOCK_OBJECTIVES: Objective[] = [{
  id: 'obj-1',
  name: 'Prevent SIF',
  description: 'Eliminate serious injuries and fatalities through proactive risk identification and rapid response.',
  type: 'safety_outcome',
  scope: 'enterprise',
  ownerRole: 'Corporate Safety Leader',
  timeHorizon: 'annual',
  startDate: '2026-01-01',
  endDate: '2026-12-31',
  status: 'active',
  priority: 1,
  primaryKpiIds: ['kpi-1', 'kpi-2', 'kpi-3'],
  supportingIndicatorIds: ['ind-6', 'ind-7'],
  actionItemIds: ['ai-1', 'ai-2']
}, {
  id: 'obj-2',
  name: 'Reduce TRIR',
  description: 'Drive total recordable incident rate below industry benchmark through improved risk management practices.',
  type: 'safety_outcome',
  scope: 'enterprise',
  ownerRole: 'Corporate Safety Leader',
  timeHorizon: 'annual',
  startDate: '2026-01-01',
  endDate: '2026-12-31',
  status: 'active',
  priority: 2,
  primaryKpiIds: ['kpi-4', 'kpi-5'],
  supportingIndicatorIds: ['ind-1', 'ind-4'],
  actionItemIds: ['ai-3', 'ai-4']
}, {
  id: 'obj-3',
  name: 'Improve Hotspot Response',
  description: 'Ensure all critical hotspots are reviewed and addressed within target timeframes.',
  type: 'operational_response',
  scope: 'project',
  ownerRole: 'Project Manager',
  timeHorizon: 'quarterly',
  startDate: '2026-04-01',
  endDate: '2026-06-30',
  status: 'active',
  priority: 3,
  primaryKpiIds: ['kpi-6', 'kpi-7'],
  supportingIndicatorIds: ['ind-3', 'ind-5'],
  actionItemIds: ['ai-5']
}];
// ─── Mock KPIs (7) ───────────────────────────────────────────
const MOCK_KPIS: KPI[] = [
// Objective 1: Prevent SIF
{
  id: 'kpi-1',
  name: 'SIF Hotspot Review Rate',
  indicatorId: 'ind-6',
  objectiveId: 'obj-1',
  scope: 'enterprise',
  owner: 'Safety Team',
  targetValue: 0,
  warningThreshold: 3,
  criticalThreshold: 5,
  baselineValue: 12,
  currentValue: 5,
  targetDate: '2026-12-31',
  evaluationPeriod: 'weekly',
  status: 'at_risk',
  actionItemIds: ['ai-1'],
  createdAt: new Date(Date.now() - 30 * 86400000),
  history: [{
    date: 'W1',
    value: 12
  }, {
    date: 'W2',
    value: 9
  }, {
    date: 'W3',
    value: 7
  }, {
    date: 'W4',
    value: 5
  }]
}, {
  id: 'kpi-2',
  name: 'Control Implementation for SIF Risks',
  indicatorId: 'ind-5',
  objectiveId: 'obj-1',
  scope: 'enterprise',
  owner: 'Site Supervisors',
  targetValue: 95,
  warningThreshold: 85,
  criticalThreshold: 75,
  baselineValue: 75,
  currentValue: 82,
  targetDate: '2026-12-31',
  evaluationPeriod: 'weekly',
  status: 'on_track',
  actionItemIds: [],
  createdAt: new Date(Date.now() - 25 * 86400000),
  history: [{
    date: 'W1',
    value: 75
  }, {
    date: 'W2',
    value: 78
  }, {
    date: 'W3',
    value: 80
  }, {
    date: 'W4',
    value: 82
  }]
}, {
  id: 'kpi-3',
  name: 'Annual SIF Count',
  indicatorId: 'ind-11',
  objectiveId: 'obj-1',
  scope: 'enterprise',
  owner: 'Corporate Safety',
  targetValue: 0,
  warningThreshold: 1,
  criticalThreshold: 2,
  baselineValue: 1,
  currentValue: 0,
  targetDate: '2026-12-31',
  evaluationPeriod: 'annual',
  status: 'achieved',
  actionItemIds: [],
  createdAt: new Date(Date.now() - 90 * 86400000),
  history: [{
    date: 'Q1',
    value: 1
  }, {
    date: 'Q2',
    value: 0
  }]
},
// Objective 2: Reduce TRIR
{
  id: 'kpi-4',
  name: 'Reduce TRIR to 2.0',
  indicatorId: 'ind-9',
  objectiveId: 'obj-2',
  scope: 'enterprise',
  owner: 'Corporate Safety',
  targetValue: 2.0,
  warningThreshold: 2.5,
  criticalThreshold: 3.0,
  baselineValue: 3.2,
  currentValue: 2.8,
  targetDate: '2026-12-31',
  evaluationPeriod: 'monthly',
  status: 'on_track',
  actionItemIds: ['ai-3'],
  createdAt: new Date(Date.now() - 60 * 86400000),
  history: [{
    date: 'M1',
    value: 3.2
  }, {
    date: 'M2',
    value: 3.0
  }, {
    date: 'M3',
    value: 2.9
  }, {
    date: 'M4',
    value: 2.8
  }]
}, {
  id: 'kpi-5',
  name: 'Mitigation Rate Target',
  indicatorId: 'ind-4',
  objectiveId: 'obj-2',
  scope: 'project',
  owner: 'Project Managers',
  targetValue: 90,
  warningThreshold: 80,
  criticalThreshold: 70,
  baselineValue: 70,
  currentValue: 78,
  targetDate: '2026-09-30',
  evaluationPeriod: 'weekly',
  status: 'on_track',
  actionItemIds: ['ai-4'],
  createdAt: new Date(Date.now() - 20 * 86400000),
  history: [{
    date: 'W1',
    value: 70
  }, {
    date: 'W2',
    value: 73
  }, {
    date: 'W3',
    value: 76
  }, {
    date: 'W4',
    value: 78
  }]
},
// Objective 3: Improve Hotspot Response
{
  id: 'kpi-6',
  name: 'Response Time Under 12h',
  indicatorId: 'ind-3',
  objectiveId: 'obj-3',
  scope: 'project',
  owner: 'Project Managers',
  targetValue: 12,
  warningThreshold: 18,
  criticalThreshold: 24,
  baselineValue: 22,
  currentValue: 14,
  targetDate: '2026-06-30',
  evaluationPeriod: 'weekly',
  status: 'at_risk',
  actionItemIds: ['ai-5'],
  createdAt: new Date(Date.now() - 15 * 86400000),
  history: [{
    date: 'W1',
    value: 22
  }, {
    date: 'W2',
    value: 18
  }, {
    date: 'W3',
    value: 16
  }, {
    date: 'W4',
    value: 14
  }]
}, {
  id: 'kpi-7',
  name: 'PTP Completion 85%',
  indicatorId: 'ind-2',
  objectiveId: 'obj-3',
  scope: 'project',
  owner: 'Safety Team',
  targetValue: 85,
  warningThreshold: 75,
  criticalThreshold: 65,
  baselineValue: 65,
  currentValue: 72,
  targetDate: '2026-06-30',
  evaluationPeriod: 'weekly',
  status: 'on_track',
  actionItemIds: [],
  createdAt: new Date(Date.now() - 15 * 86400000),
  history: [{
    date: 'W1',
    value: 65
  }, {
    date: 'W2',
    value: 68
  }, {
    date: 'W3',
    value: 70
  }, {
    date: 'W4',
    value: 72
  }]
}];
// ─── Mock Action Items (5) ───────────────────────────────────
const MOCK_ACTION_ITEMS: ActionItem[] = [{
  id: 'ai-1',
  title: 'Supervisor review for open SIF hotspots > 24h',
  description: 'Require supervisor sign-off on any SIF-flagged hotspot that remains open beyond 24 hours.',
  type: 'policy',
  triggerSource: 'objective',
  triggerSourceId: 'obj-1',
  objectiveId: 'obj-1',
  kpiId: 'kpi-1',
  owner: 'Safety Director',
  priority: 'critical',
  dueDate: '2026-05-01',
  status: 'in_progress',
  rationale: 'SIF hotspots left unaddressed beyond 24h represent unacceptable risk exposure.',
  expectedImpact: 'Reduce unresolved SIF hotspot count to 0 within target timeframe.',
  createdAt: new Date(Date.now() - 20 * 86400000)
}, {
  id: 'ai-2',
  title: 'Launch fall-protection audit program',
  description: 'Implement weekly fall-protection audits across all active projects with elevated fall risk.',
  type: 'inspection',
  triggerSource: 'objective',
  triggerSourceId: 'obj-1',
  objectiveId: 'obj-1',
  owner: 'Quality Control',
  priority: 'high',
  dueDate: '2026-05-15',
  status: 'open',
  rationale: 'Fall-protection gaps are the leading contributor to SIF-potential hotspots.',
  expectedImpact: 'Reduce fall-protection gap frequency by 40%.',
  createdAt: new Date(Date.now() - 15 * 86400000)
}, {
  id: 'ai-3',
  title: 'Improve supervisor follow-up cadence',
  description: 'Establish daily supervisor check-ins on all critical and high-severity hotspots.',
  type: 'training',
  triggerSource: 'kpi',
  triggerSourceId: 'kpi-4',
  objectiveId: 'obj-2',
  kpiId: 'kpi-4',
  owner: 'Project Managers',
  priority: 'high',
  dueDate: '2026-06-01',
  status: 'in_progress',
  rationale: 'Faster follow-up on high-severity hotspots correlates with lower incident rates.',
  expectedImpact: 'Contribute to TRIR reduction by improving response quality.',
  createdAt: new Date(Date.now() - 25 * 86400000)
}, {
  id: 'ai-4',
  title: 'Toolbox talks for repeated severe-risk trades',
  description: 'Introduce targeted toolbox talks for trades with recurring high-severity risk flags.',
  type: 'training',
  triggerSource: 'kpi',
  triggerSourceId: 'kpi-5',
  objectiveId: 'obj-2',
  kpiId: 'kpi-5',
  owner: 'Site Supervisors',
  priority: 'medium',
  dueDate: '2026-06-15',
  status: 'open',
  rationale: 'Repeated risk flags in the same trade suggest knowledge or behavior gaps.',
  expectedImpact: 'Improve mitigation rate for targeted trades by 15%.',
  createdAt: new Date(Date.now() - 10 * 86400000)
}, {
  id: 'ai-5',
  title: 'Reduce response bottleneck on night shifts',
  description: 'Add on-call safety coordinator for night shift to reduce hotspot response delays.',
  type: 'kpi_improvement',
  triggerSource: 'kpi',
  triggerSourceId: 'kpi-6',
  objectiveId: 'obj-3',
  kpiId: 'kpi-6',
  owner: 'Project Manager',
  priority: 'high',
  dueDate: '2026-05-01',
  status: 'open',
  rationale: 'Night shift response times are 2x longer than day shift, dragging down the average.',
  expectedImpact: 'Reduce average response time by 3-4 hours.',
  createdAt: new Date(Date.now() - 7 * 86400000)
}];
// ─── Context ─────────────────────────────────────────────────
interface PreventionModelContextType {
  // Indicators
  indicators: Indicator[];
  getIndicator: (id: string) => Indicator | undefined;
  // Objectives
  objectives: Objective[];
  getObjective: (id: string) => Objective | undefined;
  addObjective: (obj: Omit<Objective, 'id'>) => void;
  updateObjective: (id: string, updates: Partial<Objective>) => void;
  // KPIs
  kpis: KPI[];
  getKPI: (id: string) => KPI | undefined;
  addKPI: (kpi: Omit<KPI, 'id' | 'createdAt' | 'history'>) => void;
  updateKPI: (id: string, updates: Partial<KPI>) => void;
  removeKPI: (id: string) => void;
  promoteIndicatorToKPI: (indicatorId: string, objectiveId: string, target: number, owner: string, targetDate: string, warningThreshold?: number, criticalThreshold?: number) => void;
  // Action Items
  actionItems: ActionItem[];
  getActionItem: (id: string) => ActionItem | undefined;
  addActionItem: (item: Omit<ActionItem, 'id' | 'createdAt'>) => void;
  updateActionItem: (id: string, updates: Partial<ActionItem>) => void;
  removeActionItem: (id: string) => void;
  // Derived helpers
  getKPIsForObjective: (objectiveId: string) => KPI[];
  getIndicatorForKPI: (kpiId: string) => Indicator | undefined;
  getActionItemsForKPI: (kpiId: string) => ActionItem[];
  getActionItemsForObjective: (objectiveId: string) => ActionItem[];
  getObjectiveStatus: (objectiveId: string) => 'on_track' | 'at_risk' | 'behind' | 'achieved';
}
const PreventionModelContext = createContext<PreventionModelContextType | undefined>(undefined);
export function PreventionModelProvider({
  children
}: {
  children: ReactNode;
}) {
  const [indicators] = useState<Indicator[]>(MOCK_INDICATORS);
  const [objectives, setObjectives] = useState<Objective[]>(MOCK_OBJECTIVES);
  const [kpis, setKpis] = useState<KPI[]>(MOCK_KPIS);
  const [actionItems, setActionItems] = useState<ActionItem[]>(MOCK_ACTION_ITEMS);
  // ── Getters ──
  const getIndicator = (id: string) => indicators.find(i => i.id === id);
  const getObjective = (id: string) => objectives.find(o => o.id === id);
  const getKPI = (id: string) => kpis.find(k => k.id === id);
  const getActionItem = (id: string) => actionItems.find(a => a.id === id);
  // ── Objective CRUD ──
  const addObjective = (objData: Omit<Objective, 'id'>) => {
    setObjectives(prev => [{
      ...objData,
      id: `obj-${Date.now()}`
    }, ...prev]);
  };
  const updateObjective = (id: string, updates: Partial<Objective>) => {
    setObjectives(prev => prev.map(o => o.id === id ? {
      ...o,
      ...updates
    } : o));
  };
  // ── KPI CRUD ──
  const addKPI = (kpiData: Omit<KPI, 'id' | 'createdAt' | 'history'>) => {
    const newKPI: KPI = {
      ...kpiData,
      id: `kpi-${Date.now()}`,
      createdAt: new Date(),
      history: [{
        date: 'Start',
        value: kpiData.baselineValue
      }]
    };
    setKpis(prev => [newKPI, ...prev]);
    // Link to objective if provided
    if (kpiData.objectiveId) {
      setObjectives(prev => prev.map(o => o.id === kpiData.objectiveId ? {
        ...o,
        primaryKpiIds: [...o.primaryKpiIds, newKPI.id]
      } : o));
    }
  };
  const updateKPI = (id: string, updates: Partial<KPI>) => {
    setKpis(prev => prev.map(k => k.id === id ? {
      ...k,
      ...updates
    } : k));
  };
  const removeKPI = (id: string) => {
    const kpi = kpis.find(k => k.id === id);
    setKpis(prev => prev.filter(k => k.id !== id));
    if (kpi?.objectiveId) {
      setObjectives(prev => prev.map(o => o.id === kpi.objectiveId ? {
        ...o,
        primaryKpiIds: o.primaryKpiIds.filter(kid => kid !== id)
      } : o));
    }
  };
  const promoteIndicatorToKPI = (indicatorId: string, objectiveId: string, target: number, owner: string, targetDate: string, warningThreshold?: number, criticalThreshold?: number) => {
    const indicator = getIndicator(indicatorId);
    if (!indicator) return;
    addKPI({
      name: `${indicator.name} Target`,
      indicatorId,
      objectiveId,
      scope: indicator.scope === 'activity' ? 'project' : indicator.scope as KPI['scope'],
      owner,
      targetValue: target,
      warningThreshold,
      criticalThreshold,
      baselineValue: indicator.currentValue,
      currentValue: indicator.currentValue,
      targetDate,
      evaluationPeriod: indicator.cadence === 'monthly' ? 'monthly' : 'weekly',
      status: 'on_track',
      actionItemIds: []
    });
  };
  // ── Action Item CRUD ──
  const addActionItem = (itemData: Omit<ActionItem, 'id' | 'createdAt'>) => {
    const newItem: ActionItem = {
      ...itemData,
      id: `ai-${Date.now()}`,
      createdAt: new Date()
    };
    setActionItems(prev => [newItem, ...prev]);
  };
  const updateActionItem = (id: string, updates: Partial<ActionItem>) => {
    setActionItems(prev => prev.map(a => a.id === id ? {
      ...a,
      ...updates
    } : a));
  };
  const removeActionItem = (id: string) => {
    setActionItems(prev => prev.filter(a => a.id !== id));
  };
  // ── Derived helpers ──
  const getKPIsForObjective = (objectiveId: string) => kpis.filter(k => k.objectiveId === objectiveId);
  const getIndicatorForKPI = (kpiId: string) => {
    const kpi = getKPI(kpiId);
    return kpi ? getIndicator(kpi.indicatorId) : undefined;
  };
  const getActionItemsForKPI = (kpiId: string) => actionItems.filter(a => a.kpiId === kpiId);
  const getActionItemsForObjective = (objectiveId: string) => actionItems.filter(a => a.objectiveId === objectiveId);
  const getObjectiveStatus = (objectiveId: string): KPI['status'] => {
    const objKpis = getKPIsForObjective(objectiveId);
    if (objKpis.length === 0) return 'on_track';
    if (objKpis.every(k => k.status === 'achieved')) return 'achieved';
    if (objKpis.some(k => k.status === 'behind')) return 'behind';
    if (objKpis.some(k => k.status === 'at_risk')) return 'at_risk';
    return 'on_track';
  };
  return <PreventionModelContext.Provider value={{
    indicators,
    getIndicator,
    objectives,
    getObjective,
    addObjective,
    updateObjective,
    kpis,
    getKPI,
    addKPI,
    updateKPI,
    removeKPI,
    promoteIndicatorToKPI,
    actionItems,
    getActionItem,
    addActionItem,
    updateActionItem,
    removeActionItem,
    getKPIsForObjective,
    getIndicatorForKPI,
    getActionItemsForKPI,
    getActionItemsForObjective,
    getObjectiveStatus
  }} data-id="element-4095" data-mp="1x6vtgg46gwta">
      {children}
    </PreventionModelContext.Provider>;
}
export function usePreventionModel() {
  const context = useContext(PreventionModelContext);
  if (context === undefined) {
    throw new Error('usePreventionModel must be used within a PreventionModelProvider');
  }
  return context;
}